import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import { useBootstrap } from '../api/queries';
import { SearchSelect } from '../components/SearchSelect';
import { usePos } from './PosProvider';
import { useToast } from '../ui/ToastProvider';
import { enqueuePedido, flushOutbox } from '../offline/outbox';
import { db } from '../offline/db';
import { formatMoney, formatRate } from '../lib/format';
import type { Producto } from '../api/types';
import {
  MinusIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  XIcon,
} from '../components/icons';

interface CartLine {
  producto: Producto;
  cantidad: number;
}

export function PosModal() {
  const { open, closePos } = usePos();
  const { user, demo } = useAuth();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const { formatMoney: displayMoney } = useCurrency();
  const { data: bootstrap, isLoading } = useBootstrap();

  const [clienteId, setClienteId] = useState('');
  const [search, setSearch] = useState('');
  const moneda = 'VES' as const;
  const [observaciones, setObservaciones] = useState('');
  const [cart, setCart] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setClienteId('');
    setSearch('');
    setObservaciones('');
    setCart({});
    setSubmitting(false);
    window.setTimeout(() => searchRef.current?.focus(), 80);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePos();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, closePos]);

  const productos = bootstrap?.productos ?? [];
  const clientes = bootstrap?.clientes ?? [];
  const rates = bootstrap?.tasas ?? [];

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = term
      ? productos.filter(
          (producto) =>
            producto.nombre.toLowerCase().includes(term) ||
            producto.sku.toLowerCase().includes(term) ||
            producto.categoria.toLowerCase().includes(term),
        )
      : productos;
    return base.slice(0, 8);
  }, [productos, search]);

  const lines: CartLine[] = useMemo(
    () =>
      Object.entries(cart)
        .filter(([, cantidad]) => cantidad > 0)
        .map(([productoId, cantidad]) => {
          const producto = productos.find((item) => item.id === productoId);
          return producto ? { producto, cantidad } : null;
        })
        .filter((line): line is CartLine => line !== null),
    [cart, productos],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let impuesto = 0;
    let cajas = 0;
    for (const line of lines) {
      const base = line.producto.precioUsd * line.cantidad;
      subtotal += base;
      impuesto += base * (line.producto.impuestoPct / 100);
      cajas += line.cantidad;
    }
    const totalUsd = Math.round((subtotal + impuesto) * 100) / 100;
    const tasa = rates.find((rate) => rate.moneda === 'USD')?.tasa ?? 1;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      impuesto: Math.round(impuesto * 100) / 100,
      totalUsd,
      totalVenta: Math.round(totalUsd * tasa * 100) / 100,
      tasa,
      cajas,
    };
  }, [lines, rates]);

  const cliente = clientes.find((item) => item.id === clienteId);

  const addProduct = (producto: Producto) => {
    setCart((current) => ({ ...current, [producto.id]: (current[producto.id] ?? 0) + 1 }));
  };

  const changeQuantity = (productoId: string, delta: number) => {
    setCart((current) => {
      const next = (current[productoId] ?? 0) + delta;
      if (next <= 0) {
        const { [productoId]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [productoId]: next };
    });
  };

  const submit = async () => {
    if (!cliente) {
      notify('Selecciona un cliente para registrar el pedido.', 'warning');
      return;
    }
    if (lines.length === 0) {
      notify('Agrega al menos un producto al pedido.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const order = await enqueuePedido(
        {
          clienteId: cliente.id,
          clienteNombre: cliente.nombre,
          clienteRif: cliente.rif,
          vendedorNombre: user?.nombre ?? 'Vendedor',
          moneda,
          observaciones: observaciones.trim() || undefined,
          items: lines.map((line) => ({
            productoId: line.producto.id,
            cantidad: line.cantidad,
          })),
        },
        productos,
        rates,
      );

      let synced = false;
      if (!demo && navigator.onLine) {
        try {
          const result = await flushOutbox(false);
          synced = !result.skipped && result.errores === 0;
        } catch {
          synced = false;
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      if (synced) {
        notify(`Pedido ${order.numero} sincronizado con el servidor.`, 'success');
      } else {
        const failed = await db.outbox
          .where('orderId')
          .equals(order.id)
          .first()
          .catch(() => undefined);
        if (failed?.status === 'error' && failed.error) {
          notify(`El servidor rechazó el pedido: ${failed.error}. Quedó en cola local.`, 'warning');
        } else {
          notify(`Pedido ${order.numero} guardado en la cola local.`, 'info');
        }
      }
      closePos();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : 'No se pudo registrar el pedido.',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closePos();
      }}
    >
      <section
        className="pos-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-title"
      >
        <header className="pos-header">
          <div>
            <p className="eyebrow">Punto de venta</p>
            <h2 id="pos-title">Nuevo pedido / POS</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={closePos}
            aria-label="Cerrar punto de venta"
          >
            <XIcon size={20} />
          </button>
        </header>

        <div className="pos-body">
          <div className="pos-catalog">
            <div className="pos-section">
              <label className="field-label" htmlFor="pos-cliente">
                Cliente
              </label>
              <SearchSelect
                id="pos-cliente"
                value={clienteId}
                onChange={setClienteId}
                options={clientes.map((item) => ({
                  value: item.id,
                  label: item.nombre,
                  hint: item.rif,
                }))}
                placeholder="Buscar cliente por nombre o RIF"
                ariaLabel="Cliente"
              />
              {cliente ? (
                <p className="pos-hint">
                  {cliente.direccion ?? 'Sin dirección'} · Crédito{' '}
                  {formatMoney(cliente.limiteCredito, 'USD')}
                </p>
              ) : null}
            </div>

            <div className="pos-section">
              <label className="field-label" htmlFor="pos-producto">
                Catálogo
              </label>
              <div className="search-input">
                <SearchIcon size={17} />
                <input
                  id="pos-producto"
                  ref={searchRef}
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por nombre, SKU o categoría…"
                />
              </div>

              <div className="product-list">
                {isLoading ? <p className="muted">Cargando catálogo local…</p> : null}
                {!isLoading && filteredProducts.length === 0 ? (
                  <p className="muted">No hay productos que coincidan con la búsqueda.</p>
                ) : null}
                {filteredProducts.map((producto) => (
                  <button
                    type="button"
                    key={producto.id}
                    className="product-row"
                    onClick={() => addProduct(producto)}
                  >
                    <span className="product-row__info">
                      <strong>{producto.nombre}</strong>
                      <span>
                        {producto.sku} · Stock ruta{' '}
                        {new Intl.NumberFormat('es-VE').format(producto.stockRuta)} {producto.unidad}
                      </span>
                    </span>
                    <span className="product-row__price">
                      {formatMoney(producto.precioUsd, 'USD')}
                      <span className="product-row__add">
                        <PlusIcon size={15} />
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <aside className="pos-cart" aria-label="Carrito del pedido">
            <div className="pos-cart__head">
              <h3>Pedido</h3>
              <span className="pill pill--soft">
                {lines.length} línea{lines.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="pos-cart__lines">
              {lines.length === 0 ? (
                <div className="pos-empty">
                  <p>Todavía no hay productos.</p>
                  <span>Agrega artículos del catálogo para armar el pedido.</span>
                </div>
              ) : null}
              {lines.map((line) => (
                <div className="cart-line" key={line.producto.id}>
                  <div className="cart-line__top">
                    <strong>{line.producto.nombre}</strong>
                    <button
                      type="button"
                      className="icon-button icon-button--tiny"
                      onClick={() => changeQuantity(line.producto.id, -line.cantidad)}
                      aria-label={`Quitar ${line.producto.nombre}`}
                    >
                      <TrashIcon size={15} />
                    </button>
                  </div>
                  <div className="cart-line__bottom">
                    <div className="stepper">
                      <button
                        type="button"
                        onClick={() => changeQuantity(line.producto.id, -1)}
                        aria-label={`Restar una unidad de ${line.producto.nombre}`}
                      >
                        <MinusIcon size={14} />
                      </button>
                      <span>{line.cantidad}</span>
                      <button
                        type="button"
                        onClick={() => changeQuantity(line.producto.id, 1)}
                        aria-label={`Sumar una unidad de ${line.producto.nombre}`}
                      >
                        <PlusIcon size={14} />
                      </button>
                    </div>
                    <span className="cart-line__total">
                      {formatMoney(line.producto.precioUsd * line.cantidad, 'USD')}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="pos-cart__footer">
              <label className="field-label" htmlFor="pos-observaciones">
                Observaciones
              </label>
              <textarea
                id="pos-observaciones"
                className="input textarea"
                rows={2}
                value={observaciones}
                onChange={(event) => setObservaciones(event.target.value)}
                placeholder="Instrucciones de entrega, nota de crédito…"
              />

              <div className="pos-currency">
                <span className="pos-currency__rate">
                  Moneda: Bs. · tasa {formatRate(totals.tasa)} Bs./USD
                </span>
              </div>

              <dl className="totals">
                <div>
                  <dt>Subtotal</dt>
                  <dd>{displayMoney(totals.subtotal)}</dd>
                </div>
                <div>
                  <dt>Impuesto</dt>
                  <dd>{displayMoney(totals.impuesto)}</dd>
                </div>
                <div className="totals__grand">
                  <dt>Total Bs.</dt>
                  <dd>{formatMoney(totals.totalVenta, 'VES')}</dd>
                </div>
                <div className="totals__equiv">
                  <dt>Referencia</dt>
                  <dd>{totals.cajas} cajas · respaldo USD interno</dd>
                </div>
              </dl>

              <button
                type="button"
                className="btn btn--primary btn--block"
                onClick={() => void submit()}
                disabled={submitting || !cliente || lines.length === 0}
              >
                {submitting ? 'Guardando…' : 'Confirmar pedido'}
              </button>
              <p className="pos-footnote">
                {demo
                  ? 'Modo demo: el pedido se guarda en IndexedDB y queda en cola local.'
                  : 'Si no hay conexión, el pedido se guarda localmente y se envía al reconectar.'}
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>,
    document.body,
  );
}
